import React, { useMemo, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import * as Collapsible from '@radix-ui/react-collapsible';
import { X } from 'lucide-react';
import { cn } from '@utils/utils';
import { SidebarHeader } from './SidebarHeader';
import { Navigation } from './Navigation';
import { SidebarFooter } from './SidebarFooter';
import { Footer } from '../Footer';
import { useMessageCounter } from '@shared/contexts/MessageCounterContext';
import { getPluvialAlerts } from '@feature/pluvialRisk/services/pluvialAlerts.service';
import { PluvialAlert } from '@feature/pluvialRisk/types';
import { ROUTES } from '@shared/constants/routes.constants';
import { ConfigService } from '@shared/services/config.service';

interface SidebarProps {
  isOpen: boolean;
  onToggle: () => void;
}

export const Sidebar = React.memo<SidebarProps>(({ isOpen, onToggle }) => {
  const location = useLocation();
  const { setPluvialAlertsCount } = useMessageCounter();

  // Polling global para actualizar badge de alertas pluviales
  useEffect(() => {
    const updatePluvialAlertsBadge = async () => {
      if (location.pathname === ROUTES.MAIN.MONITOR) {
        return;
      }

      try {
        const lastVisit = localStorage.getItem('lastPluvialRiskVisit');
        const response = await getPluvialAlerts({});
        
        if (response?.data?.alerts && lastVisit) {
          const lastVisitDate = new Date(lastVisit);
          const newAlerts = response.data.alerts.filter(
            (alert: PluvialAlert) => new Date(alert.createdAt) > lastVisitDate
          );
          setPluvialAlertsCount(newAlerts.length);
        }
      } catch {
        // Error silencioso, no afecta la UI
      }
    };

    updatePluvialAlertsBadge();
    const interval = setInterval(updatePluvialAlertsBadge, 60000);

    return () => clearInterval(interval);
  }, [location.pathname, setPluvialAlertsCount]);

  const envBadge = ConfigService.getEnvironmentBadge();
  const envVariant = envBadge?.variant;

  const sidebarClasses = useMemo(() => cn(
    "hidden md:flex md:fixed md:left-4 md:top-4 md:bottom-4 md:backdrop-blur-md md:rounded-3xl md:shadow-lg md:z-30 md:flex-col md:transition-all md:duration-300",
    envVariant === 'staging' && "bg-blue-500/60 border-2 border-blue-400/80",
    envVariant === 'local' && "bg-amber-400/60 border-2 border-amber-500/80",
    !envVariant && "bg-white/40 border border-white/20",
    isOpen ? "md:w-74" : "md:w-20"
  ), [isOpen, envVariant]);

  return (
    <>
      {/* Desktop Sidebar (hidden on mobile) */}
      <Collapsible.Root
        open={isOpen}
        onOpenChange={onToggle}
        className={sidebarClasses}
      >
        {envBadge && (
          <div
            className={cn(
              'text-center text-xs font-bold tracking-widest py-1.5 text-white shrink-0',
              envBadge.variant === 'staging' ? 'bg-blue-700' : 'bg-amber-700',
              isOpen ? 'rounded-t-[1.35rem]' : 'rounded-t-3xl'
            )}
          >
            {isOpen ? envBadge.label : envBadge.shortLabel}
          </div>
        )}

        <SidebarHeader isOpen={isOpen} onToggle={onToggle} />

        <Navigation isOpen={isOpen} />

        <SidebarFooter isOpen={isOpen} />

        {isOpen && (
          <div className="mt-auto p-4">
            <Footer />
          </div>
        )}
      </Collapsible.Root>

      {/* Mobile Drawer (visible only on mobile) */}
      <div
        className={cn(
          "fixed inset-y-0 left-0 z-50 w-80 bg-white shadow-2xl flex flex-col transition-transform duration-300 transform md:hidden",
          isOpen ? "translate-x-0" : "-translate-x-full"
        )}
      >
        {envBadge && (
          <div
            className={cn(
              'text-center text-xs font-bold tracking-widest py-1.5 text-white shrink-0 w-full',
              envBadge.variant === 'staging' ? 'bg-blue-700' : 'bg-amber-700'
            )}
          >
            {envBadge.label}
          </div>
        )}

        {/* Close Button */}
        <button 
          onClick={onToggle}
          className="absolute top-8 right-4 flex items-center justify-center w-10 h-10 rounded-xl border-1 border-blue-500 bg-white text-blue-500 hover:bg-blue-50 active:scale-95 transition-all"
          aria-label="Cerrar menú"
        >
          <X className="w-5 h-5" strokeWidth={2.5} />
        </button>

        {/* Spacer for top alignment matching Close Button height */}
        <div className="h-16 shrink-0" />

        {/* Navigation */}
        <div className="flex-1 overflow-y-auto pt-2">
          <Navigation isOpen={true} isMobile={true} onItemClick={onToggle} />
        </div>

        {/* Footer Area */}
        <div className="shrink-0 flex flex-col pb-6">
          {/* Blue separator */}
          <div className="h-[1px] bg-blue-500 mx-6 mb-4"></div>
          
          {/* User Profile */}
          <SidebarFooter isOpen={true} isMobile={true} onItemClick={onToggle} />
          
          {/* Copyright info */}
          <div className="px-6 mt-4 text-center">
            <p className="text-[11px] text-[#475569] leading-normal font-normal">
              © {new Date().getFullYear()} Erlia Earth Data Lab. Todos los derechos reservados.
            </p>
            <p className="text-[11px] text-[#94a3b8] mt-0.5">
              Versión {ConfigService.getAppVersion()}
            </p>
          </div>
        </div>
      </div>
    </>
  );
});

Sidebar.displayName = 'Sidebar';
